// NOTE: Validation for the diagram contract.
//
// The schema is where the complexity budget is enforced. A figure past the
// budget is not a rendering problem to be solved with a smaller type size — it
// is two figures, and the parse error says so, so the assistant can split it
// rather than ship something unreadable. Every message is written to be acted
// on by a model, not only read by a developer.

import { z } from "zod";

import {
  MAX_DIAGRAM_ACTORS,
  MAX_DIAGRAM_EDGES,
  MAX_DIAGRAM_EVENTS,
  MAX_DIAGRAM_FOCAL_NODES,
  MAX_DIAGRAM_GROUPS,
  MAX_DIAGRAM_LABEL_LENGTH,
  MAX_DIAGRAM_LANES,
  MAX_DIAGRAM_LAYER_ITEMS,
  MAX_DIAGRAM_LAYERS,
  MAX_DIAGRAM_MATRIX_COLUMNS,
  MAX_DIAGRAM_MATRIX_ROWS,
  MAX_DIAGRAM_MESSAGES,
  MAX_DIAGRAM_NODES,
  MAX_DIAGRAM_QUADRANT_ITEMS,
  MAX_DIAGRAM_SUBLABEL_LENGTH,
  MAX_DIAGRAM_TITLE_LENGTH,
} from "../constants/diagram";
import {
  EDiagramEdgeKind,
  EDiagramEdgeStyle,
  EDiagramNodeKind,
  EDiagramType,
  EFlowchartStepKind,
  type TDiagramSpec,
  type TParseDiagramResult,
} from "../types/diagram";

const idSchema = z.string().trim().min(1).max(64);
const labelSchema = z.string().trim().min(1).max(MAX_DIAGRAM_LABEL_LENGTH);
const sublabelSchema = z
  .string()
  .trim()
  .min(1)
  .max(MAX_DIAGRAM_SUBLABEL_LENGTH);
const titleSchema = z.string().trim().min(1).max(MAX_DIAGRAM_TITLE_LENGTH);

const baseSchema = {
  title: titleSchema.optional(),
  subtitle: z.string().trim().min(1).max(140).optional(),
};

const edgeSchema = z
  .object({
    from: idSchema,
    to: idSchema,
    label: z.string().trim().min(1).max(MAX_DIAGRAM_SUBLABEL_LENGTH).optional(),
    style: z.nativeEnum(EDiagramEdgeStyle).optional(),
    kind: z.nativeEnum(EDiagramEdgeKind).optional(),
    bidirectional: z.boolean().optional(),
  })
  .strict();

interface IAddUniqueIdIssueProps {
  ids: string[];
  path: string[];
  context: z.RefinementCtx;
}

const addUniqueIdIssue = (props: IAddUniqueIdIssueProps): void => {
  const { ids, path, context } = props;

  if (new Set(ids).size === ids.length) return;

  context.addIssue({
    code: z.ZodIssueCode.custom,
    message: "Every id must be unique — two elements share the same id",
    path,
  });
};

interface IAddUnknownReferenceIssuesProps {
  ids: string[];
  edges: { from: string; to: string }[];
  path: string;
  context: z.RefinementCtx;
}

const addUnknownReferenceIssues = (
  props: IAddUnknownReferenceIssuesProps,
): void => {
  const { ids, edges, path, context } = props;
  const known = new Set(ids);

  edges.forEach((edge, index) => {
    [
      { value: edge.from, field: "from" },
      { value: edge.to, field: "to" },
    ].forEach(({ value, field }) => {
      if (known.has(value)) return;

      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: `"${value}" is not a declared id — every connection must reference an element that exists`,
        path: [path, index, field],
      });
    });
  });
};

interface IAddFocalIssueProps {
  focalCount: number;
  path: string;
  context: z.RefinementCtx;
}

// NOTE: The focal cap is the design system's one hard aesthetic rule: the
// accent colour is editorial emphasis, and spending it on five elements leaves
// the reader with no idea what the figure is about.
const addFocalIssue = (props: IAddFocalIssueProps): void => {
  const { focalCount, path, context } = props;

  if (focalCount <= MAX_DIAGRAM_FOCAL_NODES) return;

  context.addIssue({
    code: z.ZodIssueCode.custom,
    message: `At most ${MAX_DIAGRAM_FOCAL_NODES} elements may be focal — mark only what the figure is about`,
    path: [path],
  });
};

interface IRefineProps<TSpec> {
  spec: TSpec;
  context: z.RefinementCtx;
}

const architectureObject = z
  .object({
    ...baseSchema,
    type: z.literal(EDiagramType.ARCHITECTURE),
    nodes: z
      .array(
        z
          .object({
            id: idSchema,
            label: labelSchema,
            sublabel: sublabelSchema.optional(),
            group: z.string().trim().min(1).max(40).optional(),
            kind: z.nativeEnum(EDiagramNodeKind).optional(),
            focal: z.boolean().optional(),
          })
          .strict(),
      )
      .min(2)
      .max(MAX_DIAGRAM_NODES),
    edges: z.array(edgeSchema).max(MAX_DIAGRAM_EDGES),
  })
  .strict();

const refineArchitecture = (
  props: IRefineProps<z.infer<typeof architectureObject>>,
): void => {
  const { spec, context } = props;

  const ids = spec.nodes.map((node) => node.id);
  addUniqueIdIssue({ ids, path: ["nodes"], context });
  addUnknownReferenceIssues({ ids, edges: spec.edges, path: "edges", context });
  addFocalIssue({
    focalCount: spec.nodes.filter((node) => node.focal === true).length,
    path: "nodes",
    context,
  });

  const groups = new Set(
    spec.nodes
      .map((node) => node.group)
      .filter((group): group is string => group !== undefined),
  );

  if (groups.size > MAX_DIAGRAM_GROUPS) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      message: `At most ${MAX_DIAGRAM_GROUPS} groups — split the figure into an overview plus a detail diagram`,
      path: ["nodes"],
    });
  }
};

const flowchartObject = z
  .object({
    ...baseSchema,
    type: z.literal(EDiagramType.FLOWCHART),
    steps: z
      .array(
        z
          .object({
            id: idSchema,
            label: labelSchema,
            kind: z.nativeEnum(EFlowchartStepKind),
            focal: z.boolean().optional(),
          })
          .strict(),
      )
      .min(2)
      .max(MAX_DIAGRAM_NODES),
    edges: z.array(edgeSchema).max(MAX_DIAGRAM_EDGES),
  })
  .strict();

const refineFlowchart = (
  props: IRefineProps<z.infer<typeof flowchartObject>>,
): void => {
  const { spec, context } = props;

  const ids = spec.steps.map((step) => step.id);
  addUniqueIdIssue({ ids, path: ["steps"], context });
  addUnknownReferenceIssues({ ids, edges: spec.edges, path: "edges", context });
  addFocalIssue({
    focalCount: spec.steps.filter((step) => step.focal === true).length,
    path: "steps",
    context,
  });

  // NOTE: A decision that does not branch is a process step drawn as a
  // diamond — the shape then promises a choice the figure never delivers.
  spec.steps
    .filter((step) => step.kind === EFlowchartStepKind.DECISION)
    .forEach((step) => {
      const outgoing = spec.edges.filter((edge) => edge.from === step.id);

      if (outgoing.length >= 2) return;

      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Decision "${step.id}" needs at least two outgoing edges — use kind "process" if it does not branch`,
        path: ["steps"],
      });
    });
};

const sequenceObject = z
  .object({
    ...baseSchema,
    type: z.literal(EDiagramType.SEQUENCE),
    actors: z
      .array(
        z
          .object({
            id: idSchema,
            label: labelSchema,
            focal: z.boolean().optional(),
          })
          .strict(),
      )
      .min(2)
      .max(MAX_DIAGRAM_ACTORS),
    messages: z
      .array(
        z
          .object({
            from: idSchema,
            to: idSchema,
            label: z.string().trim().min(1).max(MAX_DIAGRAM_SUBLABEL_LENGTH),
            style: z.nativeEnum(EDiagramEdgeStyle).optional(),
            note: z
              .string()
              .trim()
              .min(1)
              .max(MAX_DIAGRAM_SUBLABEL_LENGTH)
              .optional(),
          })
          .strict(),
      )
      .min(1)
      .max(MAX_DIAGRAM_MESSAGES),
  })
  .strict();

const refineSequence = (
  props: IRefineProps<z.infer<typeof sequenceObject>>,
): void => {
  const { spec, context } = props;

  const ids = spec.actors.map((actor) => actor.id);
  addUniqueIdIssue({ ids, path: ["actors"], context });
  addUnknownReferenceIssues({
    ids,
    edges: spec.messages,
    path: "messages",
    context,
  });
  addFocalIssue({
    focalCount: spec.actors.filter((actor) => actor.focal === true).length,
    path: "actors",
    context,
  });
};

const swimlaneObject = z
  .object({
    ...baseSchema,
    type: z.literal(EDiagramType.SWIMLANE),
    lanes: z
      .array(z.string().trim().min(1).max(40))
      .min(2)
      .max(MAX_DIAGRAM_LANES),
    steps: z
      .array(
        z
          .object({
            id: idSchema,
            label: labelSchema,
            lane: z.string().trim().min(1).max(40),
            sublabel: sublabelSchema.optional(),
            focal: z.boolean().optional(),
          })
          .strict(),
      )
      .min(2)
      .max(MAX_DIAGRAM_NODES),
    edges: z.array(edgeSchema).max(MAX_DIAGRAM_EDGES),
  })
  .strict();

const refineSwimlane = (
  props: IRefineProps<z.infer<typeof swimlaneObject>>,
): void => {
  const { spec, context } = props;

  const ids = spec.steps.map((step) => step.id);
  addUniqueIdIssue({ ids, path: ["steps"], context });
  addUnknownReferenceIssues({ ids, edges: spec.edges, path: "edges", context });
  addFocalIssue({
    focalCount: spec.steps.filter((step) => step.focal === true).length,
    path: "steps",
    context,
  });
  addUniqueIdIssue({ ids: spec.lanes, path: ["lanes"], context });

  const lanes = new Set(spec.lanes);

  spec.steps.forEach((step, index) => {
    if (lanes.has(step.lane)) return;

    context.addIssue({
      code: z.ZodIssueCode.custom,
      message: `"${step.lane}" is not one of the declared lanes`,
      path: ["steps", index, "lane"],
    });
  });
};

const timelineObject = z
  .object({
    ...baseSchema,
    type: z.literal(EDiagramType.TIMELINE),
    events: z
      .array(
        z
          .object({
            id: idSchema,
            label: labelSchema,
            at: z.string().trim().min(1).max(24),
            sublabel: sublabelSchema.optional(),
            focal: z.boolean().optional(),
          })
          .strict(),
      )
      .min(2)
      .max(MAX_DIAGRAM_EVENTS),
  })
  .strict();

const refineTimeline = (
  props: IRefineProps<z.infer<typeof timelineObject>>,
): void => {
  const { spec, context } = props;

  addUniqueIdIssue({
    ids: spec.events.map((event) => event.id),
    path: ["events"],
    context,
  });
  addFocalIssue({
    focalCount: spec.events.filter((event) => event.focal === true).length,
    path: "events",
    context,
  });
};

const orgChartObject = z
  .object({
    ...baseSchema,
    type: z.literal(EDiagramType.ORG_CHART),
    nodes: z
      .array(
        z
          .object({
            id: idSchema,
            label: labelSchema,
            parent: idSchema.optional(),
            sublabel: sublabelSchema.optional(),
            focal: z.boolean().optional(),
          })
          .strict(),
      )
      .min(2)
      .max(MAX_DIAGRAM_NODES),
  })
  .strict();

const refineOrgChart = (
  props: IRefineProps<z.infer<typeof orgChartObject>>,
): void => {
  const { spec, context } = props;

  const ids = spec.nodes.map((node) => node.id);
  addUniqueIdIssue({ ids, path: ["nodes"], context });
  addFocalIssue({
    focalCount: spec.nodes.filter((node) => node.focal === true).length,
    path: "nodes",
    context,
  });

  const known = new Set(ids);

  spec.nodes.forEach((node, index) => {
    if (node.parent === undefined) return;

    if (node.parent === node.id) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: "A node cannot be its own parent",
        path: ["nodes", index, "parent"],
      });
      return;
    }

    if (known.has(node.parent)) return;

    context.addIssue({
      code: z.ZodIssueCode.custom,
      message: `"${node.parent}" is not a declared node id`,
      path: ["nodes", index, "parent"],
    });
  });

  if (!spec.nodes.some((node) => node.parent === undefined)) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      message:
        "An org chart needs at least one root — leave `parent` unset on the top node",
      path: ["nodes"],
    });
    return;
  }

  // NOTE: A cycle further up the chain (`a` reports to `b`, `b` reports to
  // `a`) satisfies both checks above as long as some *other* node is a root.
  // The layout only walks down from the roots, so a cyclic component would
  // never be drawn — the nodes would vanish from the figure with no error,
  // while the accessible description still counted them. Reject it instead.
  const parentById = new Map(spec.nodes.map((node) => [node.id, node.parent]));

  spec.nodes.forEach((node, index) => {
    const visited = new Set<string>([node.id]);
    let current = node.parent;

    while (current !== undefined) {
      if (visited.has(current)) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: `"${node.id}" is part of a reporting cycle — every parent chain must end at a node with no parent`,
          path: ["nodes", index, "parent"],
        });
        return;
      }

      visited.add(current);
      current = parentById.get(current);
    }
  });
};

const layersObject = z
  .object({
    ...baseSchema,
    type: z.literal(EDiagramType.LAYERS),
    layers: z
      .array(
        z
          .object({
            label: labelSchema,
            items: z
              .array(z.string().trim().min(1).max(28))
              .max(MAX_DIAGRAM_LAYER_ITEMS)
              .optional(),
            sublabel: sublabelSchema.optional(),
            focal: z.boolean().optional(),
          })
          .strict(),
      )
      .min(2)
      .max(MAX_DIAGRAM_LAYERS),
  })
  .strict();

const refineLayers = (
  props: IRefineProps<z.infer<typeof layersObject>>,
): void => {
  const { spec, context } = props;

  addFocalIssue({
    focalCount: spec.layers.filter((layer) => layer.focal === true).length,
    path: "layers",
    context,
  });
};

const quadrantAxisSchema = z
  .object({
    label: z.string().trim().min(1).max(40),
    low: z.string().trim().min(1).max(24),
    high: z.string().trim().min(1).max(24),
  })
  .strict();

const quadrantObject = z
  .object({
    ...baseSchema,
    type: z.literal(EDiagramType.QUADRANT),
    xAxis: quadrantAxisSchema,
    yAxis: quadrantAxisSchema,
    items: z
      .array(
        z
          .object({
            id: idSchema,
            label: labelSchema,
            x: z.number().min(0).max(1),
            y: z.number().min(0).max(1),
            focal: z.boolean().optional(),
          })
          .strict(),
      )
      .min(2)
      .max(MAX_DIAGRAM_QUADRANT_ITEMS),
  })
  .strict();

const refineQuadrant = (
  props: IRefineProps<z.infer<typeof quadrantObject>>,
): void => {
  const { spec, context } = props;

  addUniqueIdIssue({
    ids: spec.items.map((item) => item.id),
    path: ["items"],
    context,
  });
  addFocalIssue({
    focalCount: spec.items.filter((item) => item.focal === true).length,
    path: "items",
    context,
  });
};

const matrixAxisEntrySchema = z
  .object({
    id: idSchema,
    label: labelSchema,
  })
  .strict();

const matrixObject = z
  .object({
    ...baseSchema,
    type: z.literal(EDiagramType.MATRIX),
    rows: z.array(matrixAxisEntrySchema).min(1).max(MAX_DIAGRAM_MATRIX_ROWS),
    columns: z
      .array(matrixAxisEntrySchema)
      .min(1)
      .max(MAX_DIAGRAM_MATRIX_COLUMNS),
    // NOTE: Only the cells that hold are listed. An absent cell is "no", which
    // keeps a sparse permission model small and makes the safe reading the
    // default rather than something a spec has to remember to write out.
    cells: z
      .array(
        z
          .object({
            row: idSchema,
            column: idSchema,
            // NOTE: Shorter than a node label on purpose — it has to fit a
            // column, not a box ("read", "RWD", "n/a").
            label: z.string().trim().min(1).max(12).optional(),
            focal: z.boolean().optional(),
          })
          .strict(),
      )
      .max(MAX_DIAGRAM_MATRIX_ROWS * MAX_DIAGRAM_MATRIX_COLUMNS),
  })
  .strict();

const refineMatrix = (
  props: IRefineProps<z.infer<typeof matrixObject>>,
): void => {
  const { spec, context } = props;

  addUniqueIdIssue({
    ids: spec.rows.map((row) => row.id),
    path: ["rows"],
    context,
  });
  addUniqueIdIssue({
    ids: spec.columns.map((column) => column.id),
    path: ["columns"],
    context,
  });
  addFocalIssue({
    focalCount: spec.cells.filter((cell) => cell.focal === true).length,
    path: "cells",
    context,
  });

  const rowIds = new Set(spec.rows.map((row) => row.id));
  const columnIds = new Set(spec.columns.map((column) => column.id));

  // NOTE: A cell naming a row or column that does not exist would simply not be
  // drawn, and a figure quietly missing a grant is exactly the failure an audit
  // artefact cannot afford.
  spec.cells.forEach((cell, index) => {
    if (!rowIds.has(cell.row)) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Cell ${String(index)} names row "${cell.row}", which is not declared in rows`,
        path: ["cells", index, "row"],
      });
    }

    if (!columnIds.has(cell.column)) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Cell ${String(index)} names column "${cell.column}", which is not declared in columns`,
        path: ["cells", index, "column"],
      });
    }
  });

  // NOTE: Two cells for one intersection means the figure draws one of them and
  // silently discards the other — which of the two is an implementation detail
  // no reader could guess.
  const seen = new Set<string>();

  spec.cells.forEach((cell, index) => {
    const key = `${cell.row} ${cell.column}`;

    if (seen.has(key)) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Two cells describe the same intersection of "${cell.row}" and "${cell.column}"`,
        path: ["cells", index],
      });
    }

    seen.add(key);
  });
};

const diagramUnion = z.discriminatedUnion("type", [
  architectureObject,
  flowchartObject,
  sequenceObject,
  swimlaneObject,
  timelineObject,
  orgChartObject,
  layersObject,
  quadrantObject,
  matrixObject,
]);

// NOTE: The discriminant picks the branch, then one refine pass applies that
// branch's cross-field rules. Keeping the checks off the branches is what lets
// `discriminatedUnion` report "unknown diagram type" instead of eight parallel
// failures when the assistant sends a type that does not exist.
export const diagramSpecSchema: z.ZodType<TDiagramSpec> =
  diagramUnion.superRefine((spec, context) => {
    switch (spec.type) {
      case EDiagramType.ARCHITECTURE:
        refineArchitecture({ spec, context });
        break;
      case EDiagramType.FLOWCHART:
        refineFlowchart({ spec, context });
        break;
      case EDiagramType.SEQUENCE:
        refineSequence({ spec, context });
        break;
      case EDiagramType.SWIMLANE:
        refineSwimlane({ spec, context });
        break;
      case EDiagramType.TIMELINE:
        refineTimeline({ spec, context });
        break;
      case EDiagramType.ORG_CHART:
        refineOrgChart({ spec, context });
        break;
      case EDiagramType.LAYERS:
        refineLayers({ spec, context });
        break;
      case EDiagramType.QUADRANT:
        refineQuadrant({ spec, context });
        break;
      case EDiagramType.MATRIX:
        refineMatrix({ spec, context });
        break;
      default: {
        // NOTE: A new visual type without its cross-field rules would validate
        // on shape alone; this makes that a compile error.
        const unhandled: never = spec;
        throw new Error(
          `Unsupported diagram type: ${JSON.stringify(unhandled)}`,
        );
      }
    }
  });

// NOTE: Issues are flattened into one readable sentence per problem. The model
// receives this text verbatim when a `generateDiagram` call fails validation,
// so it has to name the field and the fix, not just the constraint.
const formatIssues = (error: z.ZodError): string =>
  error.issues
    .map((issue) => {
      const path = issue.path.join(".");
      return path.length > 0 ? `${path}: ${issue.message}` : issue.message;
    })
    .join("; ");

export const parseDiagramSpec = (input: unknown): TParseDiagramResult => {
  const result = diagramSpecSchema.safeParse(input);

  if (result.success) {
    return { success: true, data: result.data };
  }

  return { success: false, error: formatIssues(result.error) };
};

// NOTE: Only free text a reader sees is shortened. Ids and the fields that
// point at them (`from`, `to`, `parent`, `row`, `column`, `lane`, `lanes`) and
// `group`, which nodes are matched on, are left alone: cutting one of those
// would quietly disconnect or merge parts of the figure.
const SHORTENABLE_TEXT_KEYS: ReadonlySet<string> = new Set([
  "title",
  "subtitle",
  "label",
  "sublabel",
  "note",
  "at",
  "low",
  "high",
  "items",
]);

const ELLIPSIS = "…";

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

interface IShortenTextProps {
  value: string;
  maxLength: number;
}

// NOTE: Hermes (mobile) has no `Intl.Segmenter`; there the text is walked by
// code points, which still never splits a surrogate pair.
const graphemeSegmenter: Intl.Segmenter | null =
  typeof Intl.Segmenter === "function"
    ? new Intl.Segmenter(undefined, { granularity: "grapheme" })
    : null;

const splitGraphemes = (value: string): string[] =>
  graphemeSegmenter
    ? Array.from(graphemeSegmenter.segment(value), ({ segment }) => segment)
    : Array.from(value);

// NOTE: Walks whole graphemes rather than UTF-16 units so an emoji — a flag or
// a ZWJ sequence included — is never split, while the budget is still counted
// in `.length`, the unit zod's `max` measures.
const shortenText = ({ value, maxLength }: IShortenTextProps): string => {
  const budget = maxLength - ELLIPSIS.length;
  let shortened = "";

  for (const grapheme of splitGraphemes(value.trim())) {
    if (shortened.length + grapheme.length > budget) break;
    shortened += grapheme;
  }

  return `${shortened.trimEnd()}${ELLIPSIS}`;
};

interface IShortenTextAtPathProps {
  root: unknown;
  path: readonly PropertyKey[];
  maxLength: number;
}

const shortenTextAtPath = ({
  root,
  path,
  maxLength,
}: IShortenTextAtPathProps): boolean => {
  const textKey = [...path].reverse().find((key) => typeof key === "string");
  const lastKey = path.at(-1);

  if (typeof textKey !== "string" || !SHORTENABLE_TEXT_KEYS.has(textKey)) {
    return false;
  }

  let container: unknown = root;

  for (const key of path.slice(0, -1)) {
    if (Array.isArray(container) && typeof key === "number") {
      container = container[key];
    } else if (isRecord(container) && typeof key === "string") {
      container = container[key];
    } else {
      return false;
    }
  }

  if (Array.isArray(container) && typeof lastKey === "number") {
    const value: unknown = container[lastKey];
    if (typeof value !== "string") return false;
    container[lastKey] = shortenText({ value, maxLength });
    return true;
  }

  if (isRecord(container) && typeof lastKey === "string") {
    const value = container[lastKey];
    if (typeof value !== "string") return false;
    container[lastKey] = shortenText({ value, maxLength });
    return true;
  }

  return false;
};

// NOTE: Models routinely overrun a text budget by a few characters — one label
// at 65 of 60 (NSAI-6549). Strict parsing then throws away an otherwise valid
// figure and the reader gets raw JSON. For display, every over-long free-text
// field is shortened with an ellipsis and the spec is checked once more; any
// other problem still fails exactly as `parseDiagramSpec` would. Chat clients
// and the server's document export share this path, so a figure the chat drew
// is never dropped from the exported file.
export const parseDiagramSpecForDisplay = (
  input: unknown,
): TParseDiagramResult => {
  const result = diagramSpecSchema.safeParse(input);

  if (result.success) {
    return { success: true, data: result.data };
  }

  const tooBigIssues = result.error.issues.filter(
    (issue) => issue.code === z.ZodIssueCode.too_big,
  );

  if (tooBigIssues.length === 0) {
    return { success: false, error: formatIssues(result.error) };
  }

  // NOTE: The spec arrives as parsed JSON, so a JSON round-trip is a complete
  // copy — the caller's object is never mutated.
  const repaired: unknown = JSON.parse(JSON.stringify(input));
  let isRepaired = false;

  tooBigIssues.forEach((issue) => {
    if (issue.code !== z.ZodIssueCode.too_big) return;

    const isShortened = shortenTextAtPath({
      root: repaired,
      path: issue.path,
      maxLength: Number(issue.maximum),
    });

    isRepaired = isRepaired || isShortened;
  });

  if (!isRepaired) {
    return { success: false, error: formatIssues(result.error) };
  }

  return parseDiagramSpec(repaired);
};
